import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { Container } from "./Container";
import { LanguageSwitcher } from "./LanguageSwitcher";

export interface HeaderProps {
  logoImageSrc?: string;
  logoImageAlt?: string;
  className?: string;
}

const NAV_KEYS: { key: string; to: string }[] = [
  { key: "nav.home", to: "/" },
  { key: "nav.about", to: "/about-us" },
  { key: "nav.services", to: "/services" },
  { key: "nav.team", to: "/team" },
  { key: "nav.contact", to: "/contact" },
];

export function Header({
  logoImageSrc,
  logoImageAlt = "BalkanFinXpert",
  className = "",
}: HeaderProps): React.ReactElement {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  const handleNavClick = () => {
    closeMenu();
  };

  return (
    <header
      className={`bg-white overflow-x-hidden ${className}`.trim()}
      role="banner"
    >
      <Container className="!max-w-full px-4 sm:px-6 md:px-16">
        <div className="flex h-16 md:h-20 items-center justify-between gap-2 py-4 md:py-6 min-w-0">
          <Link to="/" className="shrink-0 flex items-center min-w-0">
            {logoImageSrc ? (
              <img
                src={logoImageSrc}
                alt={logoImageAlt}
                className="h-9 md:h-[50px] w-auto object-contain"
              />
            ) : (
              <span className="text-lg md:text-xl font-bold text-navy hover:opacity-90 transition-opacity truncate">
                {t("hero.title")}
              </span>
            )}
          </Link>

          <nav
            className="hidden md:flex items-center gap-6 lg:gap-8 shrink-0"
            aria-label={t("nav.home")}
          >
            {NAV_KEYS.map(({ key, to }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `text-sm lg:text-base font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive ? "text-gold" : "text-navy hover:text-gold"
                  }`
                }
              >
                {t(key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4 shrink-0 min-w-0">
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 rounded-lg text-navy hover:bg-navy/5 focus:outline-none focus:ring-2 focus:ring-navy/20"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? t("nav.close") : t("nav.menu")}
            >
              <span
                className={`block w-6 h-0.5 bg-navy rounded transition-all duration-200 ${
                  mobileMenuOpen ? "rotate-45 translate-y-1" : ""
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-navy rounded mt-1.5 transition-all duration-200 ${
                  mobileMenuOpen ? "opacity-0 -translate-x-2" : ""
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-navy rounded mt-1.5 transition-all duration-200 ${
                  mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-200 ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!mobileMenuOpen}
      >
        <div
          className="absolute inset-0 bg-navy/60"
          onClick={closeMenu}
          aria-hidden
        />
        <div
          className={`absolute top-0 right-0 bottom-0 w-full max-w-sm bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="text-navy font-semibold">{t("nav.menu")}</span>
            <button
              type="button"
              onClick={closeMenu}
              className="relative w-10 h-10 flex items-center justify-center rounded-lg text-navy hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-navy/20"
              aria-label={t("nav.close")}
            >
              <span className="sr-only">{t("nav.close")}</span>
              <span className="absolute w-5 h-0.5 bg-navy rounded rotate-45" />
              <span className="absolute w-5 h-0.5 bg-navy rounded -rotate-45" />
            </button>
          </div>
          <nav className="flex flex-col p-4 gap-1" aria-label={t("nav.home")}>
            {NAV_KEYS.map(({ key, to }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `text-lg font-medium py-3 px-4 rounded-lg transition-colors ${
                    isActive
                      ? "bg-navy/10 text-gold font-semibold"
                      : "text-navy hover:bg-navy/5 hover:text-gold"
                  }`
                }
              >
                {t(key)}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 mt-auto border-t border-gray-200">
            <p className="text-gray-500 text-sm mb-2">{t("footer.contacts")}</p>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
